import type { SortingState } from "@tanstack/react-table"

import { AP_AGING_ELIGIBLE_STATUSES, AP_AGING_SEARCH_MAX_LENGTH } from "./constants"
import {
  type ApAgingInvoice,
  ApAgingInvoiceSortField,
  type ApAgingTotals,
  type ApAgingVendor,
} from "./types/apAgingReport"
import type { ApAgingReportSearchParams } from "./types/searchParams"

import { type ApAgingReportInput, type GetApAgingReportQuery, SortingOrder } from "@/graphql/codegen/graphql"
import { INVOICE_STATUS_LABELS } from "@/screens/Accounting/subfeatures/Invoices/constants/labels"
import { upperSnakeCaseToCamelCase } from "@/utils/utils"

const DUE_DATE_CHIP_PREFIX = "Due Date: "

export const formatApAgingDateRangeChipLabel = (from: string | undefined, to: string | undefined): string => {
  if (from && to) return `${DUE_DATE_CHIP_PREFIX}${from} to ${to}`
  if (from) return `${DUE_DATE_CHIP_PREFIX}from ${from}`
  if (to) return `${DUE_DATE_CHIP_PREFIX}until ${to}`
  return ""
}

type ApAgingReportFilters = Pick<
  ApAgingReportSearchParams,
  "statuses" | "agingBuckets" | "dueDateFrom" | "dueDateTo" | "query"
>

export const toApAgingReportInput = (
  searchParams: ApAgingReportFilters,
  limit: number,
  offset: number
): ApAgingReportInput => ({
  // The backend reads an empty status list as no status at all, but on this screen an empty selection means every
  // eligible status, like an empty aging bucket selection.
  statuses: searchParams.statuses.length ? searchParams.statuses : [...AP_AGING_ELIGIBLE_STATUSES],
  agingBuckets: searchParams.agingBuckets,
  dueDateFrom: searchParams.dueDateFrom,
  dueDateTo: searchParams.dueDateTo,
  // The backend rejects a blank query and one longer than the limit: blank text means no search, long text is cut.
  query: searchParams.query?.trim().slice(0, AP_AGING_SEARCH_MAX_LENGTH) || undefined,
  offset,
  limit,
})

const INVOICE_SORT_FIELD_TO_COMPARATOR: Record<
  ApAgingInvoiceSortField,
  (a: ApAgingInvoice, b: ApAgingInvoice) => number
> = {
  [ApAgingInvoiceSortField.DaysOverdue]: (a, b) => a.daysOverdue - b.daysOverdue,
  [ApAgingInvoiceSortField.DueDate]: (a, b) => a.dueDate.localeCompare(b.dueDate),
  // Alphabetical by the status label, like the status sort of the Invoices list.
  [ApAgingInvoiceSortField.Status]: (a, b) =>
    INVOICE_STATUS_LABELS[a.status].localeCompare(INVOICE_STATUS_LABELS[b.status]),
}

// AK-6964 AC7: The backend returns the invoices of a vendor ordered by due date, so the sort the user picks is applied
// here; the invoice number keeps equal rows in a stable order.
export const sortApAgingInvoices = (
  invoices: ApAgingInvoice[],
  field: ApAgingInvoiceSortField,
  order: SortingOrder
): ApAgingInvoice[] => {
  const direction = order === SortingOrder.Desc ? -1 : 1
  const compare = INVOICE_SORT_FIELD_TO_COMPARATOR[field]

  return [...invoices].sort((a, b) => direction * compare(a, b) || a.invoiceNumber.localeCompare(b.invoiceNumber))
}

export const toApAgingTotals = ({
  current,
  days1To30,
  days31To60,
  days61To90,
  over90,
  total,
}: ApAgingTotals): ApAgingTotals => ({ current, days1To30, days31To60, days61To90, over90, total })

export type ApAgingReportResponse = Extract<
  GetApAgingReportQuery["financialReporting"]["apAgingReport"],
  { __typename: "ApAgingReportSuccess" }
>["report"]

export const toApAgingVendors = (vendors: ApAgingReportResponse["vendors"]): ApAgingVendor[] =>
  vendors.map((vendor) => ({
    vendorCompanyId: vendor.vendorCompanyId,
    vendorName: vendor.vendorCompany.name ?? "",
    vendorNumber: vendor.vendorCompany.publicId ?? "",
    as400VendorNumber: vendor.vendorCompany.externalSystemId ?? "",
    totals: toApAgingTotals(vendor.totals),
    invoices: vendor.invoices.map((invoice) => ({
      invoiceId: invoice.invoiceId,
      invoiceNumber: invoice.invoiceNumber,
      type: invoice.invoiceType,
      creationDate: invoice.creationDate,
      dueDate: invoice.dueDate,
      daysOverdue: invoice.daysOverdue,
      agingBucket: invoice.agingBucket,
      status: invoice.status,
      outstandingAmount: invoice.outstandingAmount,
    })),
  }))

export const toInvoiceSortingState = (field: ApAgingInvoiceSortField, order: SortingOrder): SortingState => [
  { id: upperSnakeCaseToCamelCase(field), desc: order === SortingOrder.Desc },
]
